import api from './api';

// --- Interfaces (mirror src/services/outbound-analyst.service.ts) ---

export type ChannelMix =
  | 'email'
  | 'linkedin_email'
  | 'linkedin_email_call'
  | 'linkedin_first'
  | 'email_first';

export type Persona = 'ic' | 'manager' | 'vp' | 'clevel' | 'tech' | 'sales' | 'marketing' | 'hr';

export interface OutboundMetrics {
  sent?: number;
  opened?: number;
  replied?: number;
  bounced?: number;
  positiveReplies?: number;
  linkedinSent?: number;
  linkedinAccepted?: number;
  messagesSent?: number;
  messagesRead?: number;
  openRate?: number;
  replyRate?: number;
  positiveReplyRate?: number;
  bounceRate?: number;
  acceptRate?: number;
  readRate?: number;
}

export interface OutboundContext {
  channelMix?: ChannelMix;
  listSize?: number;
  steps?: number;
  persona?: Persona;
}

export interface MetricVerdict {
  metric: string;
  value: number;
  level: 'bad' | 'average' | 'good' | 'great' | 'exceptional' | 'info';
  emoji: string;
  judgment: string;
  benchmark: string;
  rootCause?: string;
  fix?: string;
}

export interface BenchmarkReport {
  verdicts: MetricVerdict[];
  summary: string;
  expectedReplyRate?: number;
  metrics: Record<string, number>;
}

export interface LemlistCampaignStats {
  campaignId: string;
  byType: Record<string, number>;
  sent: number;
  opened: number;
  replied: number;
  bounced: number;
  positiveReplies: number;
  unsubscribed: number;
  linkedinSent: number;
  linkedinAccepted: number;
  linkedinReplied: number;
  linkedinMessageSent: number;
  linkedinMessageRead: number;
  messagesSent: number;
  messagesRead: number;
  totalActivities: number;
}

export interface LemlistCampaignOption {
  _id: string;
  name: string;
  status?: string;
}

export interface SignalCampaignBenchmark {
  campaignId: string;
  name: string;
  totals: { sent: number; opened: number; replied: number; positive: number };
  variants: Array<{
    id: string;
    label: string;
    angle?: string;
    sent: number;
    replied: number;
    replyRate: number;
  }>;
  report: BenchmarkReport;
}

class OutboundService {
  async benchmark(metrics: OutboundMetrics, context: OutboundContext): Promise<BenchmarkReport> {
    const response = await api.post('/outbound/benchmark', { metrics, context });
    return response.data;
  }

  async getLemlistCampaigns(): Promise<LemlistCampaignOption[]> {
    const response = await api.get('/lemlist/campaigns');
    return response.data;
  }

  async getLemlistStats(
    campaignId: string,
    persona?: string
  ): Promise<{ stats: LemlistCampaignStats; report: BenchmarkReport }> {
    const response = await api.get(`/outbound/lemlist/${campaignId}/stats`, {
      params: persona ? { persona } : {},
    });
    return response.data;
  }

  async getSignalCampaignBenchmark(id: string): Promise<SignalCampaignBenchmark> {
    const response = await api.get(`/outbound/signal-campaigns/${id}/benchmark`);
    return response.data;
  }
}

const outboundService = new OutboundService();
export default outboundService;
